/** Унифицированная кнопка для выбора настроек и команд. */
import type { ButtonHTMLAttributes } from "react";
import { Link, type LinkProps } from "react-router-dom";

const TEXT_SIZE = {
	xs: "text-xs",
	sm: "text-sm",
	base: "text-base",
	lg: "text-lg",
	xl: "text-xl",
	"2xl": "text-2xl",
	"3xl": "text-3xl",
	"4xl": "text-4xl",
	"5xl": "text-5xl",
} as const;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	active?: boolean;
	border?: boolean;
	/** Ссылка рендерится тем же визуальным контролом, что и обычная кнопка. */
	to?: LinkProps["to"];
	/** Размер текста; без него берётся `text-xs`, если размер не указан в className. */
	textSize?: keyof typeof TEXT_SIZE;
	variant?: "default" | "header" | "segmented";
};

const hasTextSizeClass = (className: string) =>
	/(?:^|\s)text-(?:xs|sm|base|lg|xl|[2-5]xl|\[\d)/.test(className);

export const Button = ({
	active,
	border = true,
	className = "",
	type = "button",
	textSize,
	to,
	variant = "default",
	...props
}: ButtonProps) => {
	const sizeClass = textSize
		? TEXT_SIZE[textSize]
		: hasTextSizeClass(className)
			? ""
			: TEXT_SIZE.xs;
	const colors =
		variant === "header"
			? active
				? "border-accent bg-accent text-overlay-ink"
				: "border-transparent bg-transparent text-ink hover:border-transparent hover:bg-accent-soft hover:text-ink"
			: variant === "segmented"
				? active
					? "bg-accent text-overlay-ink hover:bg-accent-hover"
					: "bg-transparent text-ink hover:bg-surface hover:text-accent"
				: active
					? "border-accent bg-accent-soft text-accent"
					: "border-border text-ink hover:border-accent/50 hover:bg-accent-soft";
	const buttonClassName = `rounded-md ${border ? "border" : ""} p-2 ${sizeClass} transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${colors} ${className}`;

	if (to) {
		const { disabled, ...linkProps } = props;
		return (
			<Link
				to={to}
				className={buttonClassName}
				aria-disabled={disabled || undefined}
				{...(linkProps as Omit<LinkProps, "to" | "className">)}
			/>
		);
	}

	return <button type={type} className={buttonClassName} {...props} />;
};
